import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import { useCart } from "@/context/CartContext";
import { ShopHeader } from "@/components/ShopHeader";
import { ShopFooter } from "@/components/ShopFooter";
import { StoreNav } from "@/components/StoreNav";
import { Package, Plus, CaretRight } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ShopPage() {
  const { t, lang } = useLang();
  const { add } = useCart();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const category = params.get("category") || "";
  const [cats, setCats] = useState([]);
  const [data, setData] = useState({ items: [], total: 0 });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [openCat, setOpenCat] = useState(null);

  useEffect(() => {
    axios.get(`${API}/shop/categories`).then((r) => setCats(r.data)).catch(() => {});
  }, []);

  useEffect(() => { setPage(1); }, [q, category]);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/shop/products`, { params: { q, category, page, per_page: 24 } })
      .then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [q, category, page]);

  // Flat id -> name lookup (roots + children) for the page title
  const flatCats = useMemo(() => {
    const m = {};
    cats.forEach((c) => {
      m[c.category_id] = c.name;
      (c.children || []).forEach((ch) => { m[ch.category_id] = ch.name; });
    });
    return m;
  }, [cats]);

  // Keep the accordion open on the branch that contains the active category
  useEffect(() => {
    if (!category || cats.length === 0) return;
    const parent = cats.find((c) => (c.children || []).some((ch) => ch.category_id === category));
    if (parent) setOpenCat(parent.category_id);
    else if (cats.some((c) => c.category_id === category)) setOpenCat(category);
  }, [category, cats]);

  const currentCatName = category && flatCats[category]
    ? (flatCats[category][lang] || flatCats[category].fr) : "";
  const heading = q ? `« ${q} »` : (currentCatName || t.catalog);
  const pages = Math.ceil((data.total || 0) / 24);

  return (
    <div className="min-h-screen bg-background">
      <ShopHeader />
      <div className="mx-auto max-w-[1280px] px-4 py-4"><StoreNav active="catalog" /></div>
      <div className="flex">
        <aside className="hidden w-72 shrink-0 border-r border-border p-4 lg:block" data-testid="category-sidebar">
          <div className="mb-3 text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">{t.categories}</div>
          <button onClick={() => setParams(q ? { q } : {})}
            className={`block w-full text-left px-2 py-1.5 text-sm transition-colors duration-200 ${!category ? "text-primary font-semibold" : "text-foreground hover:text-primary"}`}>
            {t.allCategories}
          </button>
          <div className="mt-1 space-y-0.5">
            {cats.map((c) => {
              const hasKids = c.children?.length > 0;
              const isOpen = openCat === c.category_id;
              const activeTop = category === c.category_id;
              return (
                <div key={c.category_id}>
                  <div className={`flex items-center rounded transition-colors duration-150 ${activeTop ? "bg-primary/10" : "hover:bg-muted"}`}>
                    <button
                      data-testid={`shop-cat-${c.category_id}`}
                      onClick={() => setParams({ category: c.category_id })}
                      className={`flex-1 truncate px-2 py-1.5 text-left text-sm transition-colors duration-200 ${activeTop ? "text-primary font-semibold" : "text-foreground hover:text-primary"}`}>
                      {c.name[lang] || c.name.fr}
                    </button>
                    {hasKids && (
                      <button
                        data-testid={`shop-cat-toggle-${c.category_id}`}
                        onClick={() => setOpenCat(isOpen ? null : c.category_id)}
                        aria-label="expand"
                        className="shrink-0 px-2 py-1.5 text-muted-foreground hover:text-primary">
                        <CaretRight size={13} weight="bold" className={`transition-transform duration-200 ${isOpen ? "rotate-90" : ""}`} />
                      </button>
                    )}
                  </div>
                  {hasKids && isOpen && (
                    <div className="mt-0.5 space-y-0.5 border-l border-border pl-2" data-testid={`shop-subcats-${c.category_id}`}>
                      {c.children.map((ch) => (
                        <button key={ch.category_id} onClick={() => setParams({ category: ch.category_id })}
                          className={`block w-full truncate rounded pl-4 pr-2 py-1 text-left text-xs transition-colors duration-150 ${category === ch.category_id ? "bg-primary/5 text-primary font-semibold" : "text-muted-foreground hover:bg-muted hover:text-primary"}`}>
                          {ch.name[lang] || ch.name.fr}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </aside>

        <main className="flex-1 p-6">
          <div className="mb-6 flex items-end justify-between">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-primary">{t.products}</div>
              <h1 className="mt-1 font-display text-3xl font-black tracking-tighter">
                {heading}
              </h1>
              <div className="mt-1 text-xs text-muted-foreground">{data.total} {t.products.toLowerCase()}</div>
            </div>
          </div>

          {loading && <div className="py-20 text-center text-muted-foreground">{t.loading}</div>}
          {!loading && data.items.length === 0 && <div className="py-20 text-center text-muted-foreground">{t.noProducts}</div>}

          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-4" data-testid="product-grid">
            {data.items.map((p) => (
              <div key={p.id} data-testid={`product-card-${p.product_id}`} className="group flex h-full flex-col rounded-lg border border-border bg-card p-4 transition-shadow duration-200 hover:shadow-md">
                <button onClick={() => navigate(`/product/${p.product_id}`)} className="relative mb-3 aspect-square overflow-hidden rounded-md bg-white">
                  {p.image ? (
                    <img src={p.image} alt={p.name[lang] || p.name.fr} loading="lazy"
                      className="h-full w-full object-contain transition-transform duration-200 group-hover:scale-105"
                      onError={(e) => { e.currentTarget.style.display = "none"; }} />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-muted-foreground"><Package size={36} /></div>
                  )}
                </button>
                <div className="font-mono text-[11px] text-primary">{p.product_code}</div>
                <button onClick={() => navigate(`/product/${p.product_id}`)}
                  className="mt-1 mb-4 line-clamp-2 min-h-[2.5rem] text-left text-sm font-medium leading-tight hover:text-primary transition-colors duration-200">
                  {p.name[lang] || p.name.fr}
                </button>
                <button data-testid={`add-to-cart-${p.product_id}`}
                  onClick={() => { add(p, 1); toast.success(t.addedToCart); }}
                  className="mt-auto flex items-center justify-center gap-2 rounded-md bg-primary py-2.5 text-xs font-bold uppercase tracking-wider text-white transition-transform duration-200 ease-out hover:-translate-y-0.5">
                  <Plus size={14} weight="bold" /> {t.addToCart}
                </button>
              </div>
            ))}
          </div>

          {pages > 1 && (
            <div className="mt-6 flex items-center justify-center gap-2">
              <button disabled={page <= 1} onClick={() => setPage(page - 1)}
                className="border border-border px-3 py-1.5 text-xs font-bold disabled:opacity-40">←</button>
              <span className="text-xs text-muted-foreground">{page} / {pages}</span>
              <button disabled={page >= pages} onClick={() => setPage(page + 1)}
                className="border border-border px-3 py-1.5 text-xs font-bold disabled:opacity-40">→</button>
            </div>
          )}
        </main>
      </div>
      <ShopFooter />
    </div>
  );
}
